import React, { useContext } from 'react';
import { AuthContext } from '../../../context/AuthContext';
import { AsyncState, StatusBadge } from '../components';
import { normalizeAdminApiError } from '../errors';
import { can } from '../permissions';
import { useEntitlementDiagnostics } from './hooks';
import type { EntitlementDiagnostic } from './types';

const date = (value: string | null) => value ? new Date(value).toLocaleString() : 'Not set';
const label = (value: string | null) => value ? value.replace(/_/g, ' ') : 'None';

function EntitlementCard({ item }: { item: EntitlementDiagnostic }) {
    const warning = item.courseRecordMissing || Boolean(item.dataQualityIssue);

    return (
        <article
            className={`rounded-[13px] border p-4 ${
                warning
                    ? 'border-amber-200 bg-amber-50/35'
                    : 'border-slate-200 bg-white'
            }`}
        >
            <div className="flex items-start justify-between gap-4 max-sm:flex-col">
                <div className="min-w-0">
                    <h3 className="m-0 truncate text-[15px] font-bold text-slate-900">
                        {item.courseName || 'Missing course record'}
                    </h3>
                    <code className="mt-1 block max-w-full truncate text-[11px] text-slate-400" title={item.coursePublicId}>
                        {item.coursePublicId}
                    </code>
                </div>
                <StatusBadge
                    status={item.normalizedStatus}
                    tone={item.allowed ? 'success' : warning ? 'warning' : 'danger'}
                />
            </div>

            <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {[
                    ['Decision', item.allowed ? 'Mobile access allowed' : 'Mobile access denied'],
                    ['Source status', label(item.sourceStatus)],
                    ['Reason', label(item.reason)],
                    ['Starts', date(item.startsAt)],
                    ['Ends', date(item.endsAt)],
                ].map(([key, value]) => (
                    <div key={key} className="rounded-xl border border-slate-100 bg-slate-50/70 px-3.5 py-3">
                        <dt className="text-[10px] font-semibold uppercase tracking-[0.06em] text-slate-400">{key}</dt>
                        <dd className="mb-0 ml-0 mt-1 text-[13px] font-semibold capitalize text-slate-700">{value}</dd>
                    </div>
                ))}

                <div className="min-w-0 rounded-xl border border-slate-100 bg-slate-50/70 px-3.5 py-3">
                    <dt className="text-[10px] font-semibold uppercase tracking-[0.06em] text-slate-400">Enrollment ID</dt>
                    <dd className="mb-0 ml-0 mt-1 min-w-0">
                        <code className="block truncate text-[11px] font-medium text-slate-600" title={item.enrollmentPublicId}>
                            {item.enrollmentPublicId}
                        </code>
                    </dd>
                </div>
            </dl>

            {warning && (
                <div className="mt-3 flex flex-col gap-1 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3" role="note">
                    <strong className="text-[12px] font-bold text-amber-800">Data-quality warning</strong>
                    <span className="text-[12px] leading-5 text-amber-700">
                        {item.courseRecordMissing
                            ? 'The referenced course record is missing.'
                            : label(item.dataQualityIssue)}
                    </span>
                </div>
            )}
        </article>
    );
}

export function EntitlementDiagnosticsPanel({ studentId }: { studentId: string }) {
    const { user } = useContext(AuthContext);
    const permitted = can(user?.role, 'mobile:manage');
    const query = useEntitlementDiagnostics(studentId, permitted);

    if (!permitted) return null;

    const error = query.error ? normalizeAdminApiError(query.error) : null;
    const items = query.data?.data.entitlements || [];

    return (
        <section
            className="overflow-hidden rounded-[16px] border border-slate-200 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.04)]"
            aria-labelledby="entitlement-panel-title"
        >
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4 max-sm:flex-col">
                <div>
                    <p className="m-0 text-[10px] font-bold uppercase tracking-[0.12em] text-blue-600">Read-only diagnostic</p>
                    <h2 id="entitlement-panel-title" className="mb-0 mt-1 text-[17px] font-bold text-slate-950">Mobile Entitlements</h2>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1.5 text-[11px] font-semibold text-slate-500">
                    No changes are made here
                </span>
            </div>

            <div className="p-5">
                <p className="mb-4 mt-0 max-w-[980px] text-[13px] leading-5 text-slate-500">
                    These decisions reflect normalized mobile access. Correct source enrollment or course records through the existing authorized workflows.
                </p>

                <AsyncState
                    loading={query.isLoading}
                    error={error}
                    onRetry={() => query.refetch()}
                    empty={!query.isLoading && !error && items.length === 0}
                    emptyTitle="No enrollment diagnostics"
                    emptyMessage="This student has no enrollment records available for mobile entitlement evaluation."
                >
                    <div className="space-y-3">
                        {items.map(item => (
                            <EntitlementCard key={item.enrollmentPublicId} item={item} />
                        ))}
                    </div>
                </AsyncState>
            </div>
        </section>
    );
}
